import moment from 'moment';
import ZdhcService, { toMatchShape } from '../services/Zdhc/ZdhcService';
import ProductService from '../services/ProductService';
import ZdhcProductService from '../services/ZdhcProductService';
import IncheckReferenceService from '../services/IncheckReferenceService.js';
import GoogleService from '../services/GoogleService.js';
import { reactive, ref } from 'vue';
import { resolveProductName } from '../utils/productName';
import { associatedReferenceId } from '../utils/matchStatus';

/**
 * Cio' che resta del matching lato browser.
 *
 * **La risoluzione non abita piu' qui.** A decidere lo stato di un periodo e' il
 * risolutore nel backend, che lo persiste su `incheck_references.match_status`;
 * la dashboard lo legge e basta (`utils/matchStatus`). Con la cascata sono
 * caduti il ponte legacy sul PID e la verifica del reference id, che erano una
 * seconda implementazione della stessa logica — e due implementazioni della
 * stessa logica divergono.
 *
 * Quello che resta e' di altra natura: l'allineamento della cache locale
 * `zdhc_products` per il flusso inventari, e le tre fasi di ricerca dei modali.
 */
const useMatchAlgorithm = () =>
{
    /**
     *
     * Data
     *
     */
    const counter = ref();
    const product = reactive({});
    const { storeExactMatch } = ProductService();
    const { translate } = GoogleService();
    const { storeZdhcProduct, deleteZdhcProduct } = ZdhcProductService();
    const { searchProducts, showProduct } = ZdhcService();
    const { updatePreviousResults, setNoMatchInIncheckReference } = IncheckReferenceService();
    /**
     *
     * Methods
     *
     */
    const assignProduct = async(p) =>
    {
        Object.assign(product, p);
    }
    const searchByWord = async (word) =>
    {
        // The new API rejects searches under 3 characters: skip locally.
        if(!word || String(word).trim().length < 3) return [];
        const res = await searchProducts({ search: String(word).trim() });
        if(!res || res.error || !Array.isArray(res.data)) return [];
        return res.data.map(toMatchShape);
    };
    /**
     * Persists the local no-match on a period reference for any product
     * shape. `extra` carries flow-specific payload keys (e.g. the
     * stale_reference marker). Returns the updated reference, or null.
     */
    const markReferenceNoMatch = async (incheckReferenceId, targetProduct, extra = {}) =>
    {
        const noMatchRes = await setNoMatchInIncheckReference(incheckReferenceId, {
            no_match: true,
            product_name: resolveProductName(targetProduct),
            formulator_name: targetProduct.formulator ? targetProduct.formulator.name : (targetProduct.formulator_name ?? null),
            new_results: null,
            ...extra
        });
        return noMatchRes?.incheck_reference ?? null;
    };
    const checkExactMatch = async () =>
    {
        let hasInventory = checkForInventory(product);
        if(!hasInventory)
        {
            if(product.formulator || product.formulator_name)
            {
                let formulator_name = product.formulator ? product.formulator.name : product.formulator_name;
                const searchRes = await searchProducts({
                    search: resolveProductName(product),
                    formulator_name: formulator_name
                });
                if(searchRes && !searchRes.error && Array.isArray(searchRes.data))
                {
                    const res = searchRes.data.map(toMatchShape);
                    let availableMatches = res.filter(el =>
                    {
                        return el.fullProduct.productName.toLowerCase() == resolveProductName(product).toLowerCase() && el.fullProduct.formulatorName.toLowerCase() == formulator_name.toLowerCase();
                    });
                    if(availableMatches.length == 1)
                    {
                        let true_match = availableMatches[0];
                        true_match.reference_date = product.incheck_reference.reference_date;
                        const storeRes = await storeExactMatch(product.id, true_match);
                        if(storeRes)
                        {
                            product.incheck_references = storeRes.data.p.incheck_references;
                            product.incheck_reference = storeRes.data.p.incheck_reference;
                            product.formulator = storeRes.data.p.formulator;
                        }
                        return {
                            status: 'ok',
                            product: product,
                            productStatus: 'success'
                        };

                    }
                }
            }
            if(product.incheck_reference.no_match || product.incheck_reference.no_match_id)
            {
                /* If product has NoMatch status, check for new ZDHC products */
                return checkNoMatchNewAssociables();
            }

            return {
                status: 'ko',
            };
        }
        else
        {
            if(product.incheck_reference.match)
            {
                return {
                    status: 'ok',
                    productStatus: 'success'
                }
            }
            else if(product.incheck_reference.suggestion_id)
            {
                return {
                    status: 'ok',
                    productStatus: 'suggestion'
                }
            }
            else if(product.incheck_reference.no_match || product.incheck_reference.no_match_id)
            {
                return {
                    status: 'ok',
                    productStatus: 'manualNoMatch'
                }
            }
        }
    };
    /**
     * True when the target product already has an inventory for the
     * reference's period. `finalizedOnly` restricts the check to frozen
     * reports (submitted/published/declined + legacy null status): a local
     * draft is retryable, so it must not freeze the stale invalidation —
     * mirrors Inventory::scopeFinalized on the backend.
     */
    const hasInventoryForReference = (targetProduct, reference, { finalizedOnly = false } = {}) =>
    {
        if(!targetProduct?.inventories?.length || !reference?.reference_date) return false;
        let incheck_date = moment(reference.reference_date, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DD');
        return targetProduct.inventories.some( i =>
        {
            if(finalizedOnly && i.status === 'draft') return false;
            let inventory_date = moment(i.reference_date, 'YYYY-MM-DDTHH:mm:ss').format('YYYY-MM-DD');
            return moment(inventory_date).isSame(moment(incheck_date));
        });
    };
    const checkForInventory = () =>
    {
        if(!Object.keys(product).length) return false;
        return hasInventoryForReference(product, product.incheck_reference);
    };
    // Search for matches - first phase
    const searchMatchesFirstPhase = async () =>
    {
        let matches = [];
        let matchId = '';
        if(product.incheck_reference?.zdhc_product_id) matchId = product.incheck_reference.zdhc_product_id
        else if(product.incheck_reference?.suggestion_id)  matchId = product.incheck_reference.suggestion.zdhc_product_id;

        const zdchRes = await searchByWord(resolveProductName(product));
        zdchRes.forEach( p => {if(p.id === matchId || p.formulator_name !== 'No match Formulator') matches.push(p);});
        return matches;
    };
    const searchMatchesSecondPhase = async () =>
    {
        let matches = [];
        // 1 - Translate the product name (EN) — kept as fallback until the
        // coverage of local_language_names is proven on the field (plan §4.4)
        let googleRes = await translate(resolveProductName(product));
        if(googleRes)
        {
            let translated_name = googleRes.data.translations[0].translatedText;
            // 2 - Search match into zdhc records
            const zdhcRes = await searchByWord(translated_name);
            zdhcRes.forEach( p => matches.push(p));
        }
        return matches;
    };
    const searchMatchesThirdPhase = async () =>
    {
        let matches = [];
        let splittedName = resolveProductName(product).split(' ');

        // Min 3 chars per search term in the new API
        splittedName = splittedName.filter( s => s.length > 2);

        const totalLength =  splittedName.length;
        let actualResLength = 0;
        if(totalLength)
        {
            for (const splitted_word of splittedName)
            {
                // Get matches for 'word'
                const zdhcRes = await searchByWord(splitted_word);
                actualResLength +=1;
                zdhcRes.forEach( p => matches.push(p));
                if(actualResLength === totalLength) return matches;
              }
        }
        return matches;
    };
    /**
     * Refreshes the local zdhc_products cache for matched products. Legacy
     * GUID lookups are gone: only products that already carry a
     * product_reference_id (new matches, or re-matched by the migration
     * script) can be refreshed — the others are counted and skipped, the
     * batch re-match owns them (plan §4.3, E10).
     */
    const refreshZdhcProductsCache = async (products) =>
    {
        counter.value = 0;
        try
        {
            for(let i = 0; i < products.length; i++)
            {
                counter.value += 1;
                let p = products[i];
                let incheck_reference = p.incheck_references[0];
                let referenceId = associatedReferenceId(incheck_reference);

                if(referenceId)
                {
                    const res = await showProduct(referenceId);
                    if(res && res.error === 'zdhc_not_found')
                    {
                        // Product gone from the catalogue (plan E11): the
                        // period must not stay matched (the inventory modal
                        // would auto-select it and send the dead reference
                        // id), the marker keeps the probe alive.
                        //
                        // E' l'ultima scrittura automatica rimasta nel browser,
                        // e duplica cio' che il risolutore fa gia' quando la
                        // verifica del reference id torna `zdhc_not_found`. Resta
                        // qui perche' e' il flusso inventari a dipenderne — la
                        // modale ricarica subito dopo — e va tolta con quello,
                        // non con la lettura dello stato. Awaited:
                        // the modal reloads products right after this
                        // refresh and must see the updated state. Only
                        // finalized periods are frozen (the api guards this
                        // too): a local draft stays retryable and must
                        // allow the invalidation.
                        if(!hasInventoryForReference(p, incheck_reference, {finalizedOnly: true}))
                        {
                            await markReferenceNoMatch(incheck_reference.id, p, {stale_reference: true});
                        }
                        await deleteZdhcProduct(incheck_reference.match?.id ?? referenceId);
                    }
                    else if(res && !res.error && res.data)
                    {
                        // Store Zdhc Product
                        const shaped = toMatchShape(res.data);
                        let zp = {
                            productName: shaped.fullProduct.productName,
                            ProductID: shaped.id,
                            product_reference_id: shaped.product_reference_id,
                            formulatorName: shaped.fullProduct.formulatorName,
                            formulator_reference_id: shaped.fullProduct.formulator_reference_id,
                            conformance_level_name: shaped.fullProduct.conformance_level_name,
                            ctz_level_name: shaped.fullProduct.ctz_level_name,
                            registered: 'ZDHC Gateway',
                            zdhcPID: shaped.fullProduct.zdhcPID,
                        };
                        translate(zp.productName)
                        .then( async (translation_res) =>
                        {
                            let translated_name = translation_res.data.translations[0].translatedText;
                            let detected_lang = translation_res.data.translations[0].detectedSourceLanguage;
                            if(zp.productName.toLowerCase() === translated_name.toLowerCase())
                            {
                                zp.translated_names = { [detected_lang] :  zp.productName.toLowerCase() };
                            }
                            else
                            {
                                zp.translated_names =
                                {
                                    [detected_lang] :  zp.productName.toLowerCase(),
                                    'en' : translated_name.toLowerCase(),
                                };
                            }
                            storeZdhcProduct(zp);
                        });
                    }
                }
            }
            if(counter.value == products.length) return {status: 'success'};
            else return {status: 'error'};
        }
        catch(e)
        {
            if(e.message)
            {
                if(e.message == 'canceled') return {status: 'aborted'};
            }
            return {status: 'error'};
        }
    };
    const checkNoMatchNewAssociables =  async () =>
    {
        let referenceIds = [];
        let res;
        res = await searchMatchesFirstPhase();
        if(res.length)
        {
            res.forEach(p => {if(!referenceIds.includes(p.product_reference_id)) referenceIds.push(p.product_reference_id)});
        }
        else
        {
            res = await searchMatchesSecondPhase();
            if(res.length)
            {
                res.forEach(p => {if(!referenceIds.includes(p.product_reference_id)) referenceIds.push(p.product_reference_id)});
            }
            else
            {
                res = await searchMatchesThirdPhase();
                if(res.length)
                {
                    res.forEach(p => {if(!referenceIds.includes(p.product_reference_id)) referenceIds.push(p.product_reference_id)});
                }
            }
        }

        if(referenceIds)
        {
            const res = await updatePreviousResults(product.incheck_reference.id, {new_results: [...referenceIds]});
            if('incheck_reference' in res)
            {
                product.incheck_reference = res.incheck_reference;
                return {
                    status: 'ok',
                    product: product,
                };
            }
        }
        return {
            status: 'ko',
        };
    }

    return {
        /* Data */
        counter,
        /* Methods */
        assignProduct,
        checkExactMatch,
        searchMatchesFirstPhase,
        searchMatchesSecondPhase,
        searchMatchesThirdPhase,
        refreshZdhcProductsCache,
        /* Legacy aliases kept so existing consumers keep working until the
           dead-code sweep of step 2. checkForExistingZdhcProduct was
           destructured by ProductsTable but never actually exported by the
           legacy composable: the alias also fixes that latent crash. */
        checkForExistingZdhcProduct: refreshZdhcProductsCache,
        checkForExistingZdhcProductV2: refreshZdhcProductsCache,
        checkForExistingZdhcProductV5: refreshZdhcProductsCache
    };
};

export default useMatchAlgorithm;
